/**
 * HTML attribute record → React props. Shared by server components and
 * client islands (pure, no React import), so element attributes captured by
 * the parser can be spread onto JSX on either side of the RSC boundary.
 */

const RENAMED: Record<string, string> = {
  class: "className",
  for: "htmlFor",
  tabindex: "tabIndex",
  colspan: "colSpan",
  rowspan: "rowSpan",
  frameborder: "frameBorder",
  allowfullscreen: "allowFullScreen",
  autoplay: "autoPlay",
  playsinline: "playsInline",
  crossorigin: "crossOrigin",
  srcset: "srcSet",
  referrerpolicy: "referrerPolicy",
  fetchpriority: "fetchPriority",
  maxlength: "maxLength",
  readonly: "readOnly",
  contenteditable: "contentEditable",
  accesskey: "accessKey",
  usemap: "useMap",
  datetime: "dateTime",
  enctype: "encType",
  hreflang: "hrefLang",
  inputmode: "inputMode",
};

const BOOLEAN = new Set([
  "allowFullScreen",
  "autoPlay",
  "muted",
  "playsInline",
  "loop",
  "controls",
  "hidden",
  "disabled",
  "readOnly",
  "required",
  "checked",
  "selected",
  "multiple",
  "open",
  "async",
  "defer",
  "novalidate",
]);

/** Attributes React would warn about or that must not be spread verbatim. */
const SKIPPED = new Set(["style", "xmlns"]);

export type DomProps = Record<string, string | boolean | undefined>;

export function attrsToProps(attrs: Record<string, string> | undefined, extraClassName?: string | false | null): DomProps {
  const props: DomProps = {};
  if (attrs) {
    for (const [name, value] of Object.entries(attrs)) {
      if (SKIPPED.has(name) || name.startsWith("on")) continue;
      const key = RENAMED[name] ?? name;
      if (BOOLEAN.has(key)) {
        props[key] = value !== "false";
        continue;
      }
      props[key] = value;
    }
  }
  const cls = cx(props.className as string | undefined, extraClassName || undefined);
  if (cls) props.className = cls;
  else delete props.className;
  return props;
}

/** Joins class names, dropping empty values and duplicate tokens (order preserved). */
export function cx(...parts: Array<string | false | null | undefined>): string {
  const seen = new Set<string>();
  for (const part of parts) {
    if (typeof part !== "string") continue;
    for (const token of part.split(/\s+/)) if (token) seen.add(token);
  }
  return [...seen].join(" ");
}

/** Copy of `attrs` without the given attribute names. */
export function omit(attrs: Record<string, string> | undefined, names: string[]): Record<string, string> {
  const out: Record<string, string> = {};
  if (!attrs) return out;
  for (const [k, v] of Object.entries(attrs)) if (!names.includes(k)) out[k] = v;
  return out;
}
